/// Hypsometric-tinted elevation contours. Five elevation bands, each with a
/// distinct hue (green low, yellow-green hills, yellow-tan plateau, orange
/// slopes, red peaks), per-band line widths (1.5pt low → 3.0pt peaks), and
/// cyan elevation labels placed along long contours. Mirrors the iOS
/// `GameScene.drawContours` rendering, including the post-2.4 visibility
/// refresh (commit d39648a on branch 2.4).
///
/// Sits between BasemapLayer and the rest (rivers / overlay / buildings /
/// terrain) in the RadarScene add-order, so the colour bands lie under every
/// other visual — they suggest terrain without competing with rings, runways,
/// or aircraft.
///
/// Background: the previous palette lived in a single warm-brown family
/// across all five bands. Saturation-only differences made "1,000" and
/// "3,000" hard to distinguish at a glance and low-elevation labels (cyan
/// now, but had been brown-on-black before) were unreadable. The hypsometric
/// convention used by every paper sectional chart gives each band its own
/// hue, which reads instantly. User feedback after the iOS 2.3 ship.

import { Container, Graphics, Text } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type {
  Feature,
  FeatureCollection,
  LineString,
} from '../types/GeoJSON'

interface Band {
  /// Upper bound of the band (ft AGL, exclusive). The last band uses Infinity.
  maxFt: number
  color: number
  fillAlpha: number
  strokeAlpha: number
  /// Stroke width for closed polygon rings in this band. Open contour lines
  /// render 0.5pt thinner than the closed value (preserves the existing
  /// open-vs-closed visual proportion). Per-band scaling means the contours
  /// you most need to see (mountain peaks) are visually heaviest.
  lineWidth: number
}

/// Bands chosen to match the post-2.4-77c41d6 iOS palette. Hypsometric tint
/// with finer resolution at high altitudes — mountain levels span 5k–14k ft
/// and the previous 5-band scheme (with 5k–8k and 8k+ as the only "high"
/// bands) read as a single orange-red wash on KTEX-style terrain. Adding
/// two intermediate bands and pushing the peaks band to darker, deeper red
/// gives the lightness gradient real work to do — "higher = darker" reads
/// as denser / warning. Coastal levels still get the green / yellow-green /
/// yellow-tan detail unchanged. Order is low → high so later bands paint on
/// top of earlier ones.
const BANDS: Band[] = [
  // 0–1k ft: dark green (rolling terrain)
  { maxFt: 1000, color: 0x4d8c40, fillAlpha: 0.25, strokeAlpha: 0.9, lineWidth: 1.5 },
  // 1k–3k ft: yellow-green (hills)
  { maxFt: 3000, color: 0xa6b340, fillAlpha: 0.35, strokeAlpha: 0.95, lineWidth: 1.5 },
  // 3k–5k ft: yellow-tan (high plateau)
  { maxFt: 5000, color: 0xe6bf4d, fillAlpha: 0.45, strokeAlpha: 1.0, lineWidth: 2.0 },
  // 5k–7k ft: amber (foothills, low mountain valleys) — held at full
  // intensity; this is the lightness peak of the high-altitude bands.
  { maxFt: 7000, color: 0xffa633, fillAlpha: 0.45, strokeAlpha: 1.0, lineWidth: 2.0 },
  // 7k–9k ft: brick orange (valley floors at mountain airports) —
  // markedly darker than amber above, signalling "crossing into
  // mountain country." Was 0xf28033 / fillAlpha 0.5 in the first
  // pass; bumped per user feedback after testing KTEX on iOS, where
  // the lighter palette was washing out at high altitudes. Mirrors
  // iOS commit fcee5d2 on branch 2.4.
  { maxFt: 9000, color: 0xcc591a, fillAlpha: 0.4, strokeAlpha: 1.0, lineWidth: 2.5 },
  // 9k–11k ft: deep red (mid slopes) — was red-orange 0xf25933 /
  // fillAlpha 0.55; now much darker, distinctly red rather than
  // red-orange. Lightness drop from brick orange above is
  // significant (~L48 → ~L32).
  { maxFt: 11000, color: 0x8c1f1a, fillAlpha: 0.4, strokeAlpha: 1.0, lineWidth: 2.5 },
  // 11k+ ft: near-black maroon (peaks) — was 0xb32633 / fillAlpha
  // 0.6; now almost black with a deep red tint so mountain peaks
  // read instantly as "danger" by lightness alone.
  { maxFt: Infinity, color: 0x400d1a, fillAlpha: 0.45, strokeAlpha: 1.0, lineWidth: 3.0 },
]

/// `first ≈ last` tolerance in degrees — matches the iOS reader. Anything
/// inside this gets treated as a closed ring (fillable); everything else is
/// an open contour (stroke only).
const CLOSED_TOLERANCE_DEG = 0.001

/// Label cadence along long contours. A 600-point ridge gets ~4 labels
/// spread along its length so a label is usually close to wherever the
/// player is looking; shorter contours get one label at the midpoint.
const LABEL_STRIDE_ALONG_CONTOUR = 150
/// Below this length, just put one label at the midpoint (no benefit to
/// multi-labelling a short line that fits in a small area).
const MIN_COORDS_FOR_LABEL = 10

/// Bright cyan label colour — reads as "information" and contrasts cleanly
/// against every band of the hypsometric ramp. The iOS equivalent is
/// `(0.30, 0.85, 0.95)`; this is the integer-RGB form.
const LABEL_COLOR = 0x4dd9f2
const LABEL_FONT_SIZE = 16

/// Two-step zoom-based label culling thresholds. Pixi-viewport scale
/// is inverted from SpriteKit's — smaller scale = more zoomed-out —
/// so each constant reads as "if world is < N% of native size, do X."
///
/// At LUKLA / Telluride / Innsbruck the Himalayan / alpine terrain
/// produces dozens of contour features in the 15–20k ft band; without
/// staged culling the zoomed-out view drowns in labels.
///
///   - Below `MID_ZOOM`:  hide labels under MID_CUTOFF_FT (10k) and
///                        keep only ONE label per long contour
///                        (decimated_at_low_zoom).
///   - Below `FAR_ZOOM`:  hide labels under FAR_CUTOFF_FT (15k) — the
///                        zoom-all-the-way-out view shows peaks only.
const MID_ZOOM_CULL_VIEWPORT_SCALE = 0.4
const FAR_ZOOM_CULL_VIEWPORT_SCALE = 0.25
const MID_CUTOFF_FT = 10000
const FAR_CUTOFF_FT = 15000

export class ContoursLayer extends Container {
  /// Parallel collection of label containers + their elevations.
  /// `updateLabelStyles` mutates each container's `scale` and
  /// `visible` based on the current viewport zoom — counter-scaling
  /// neutralises the viewport's transform so labels render at a
  /// consistent screen-space size at any zoom level. Without this,
  /// at the typical zoom-out-to-fit-world view (~0.3 scale on a
  /// phone) the 16pt labels render as ~5px tall on screen and
  /// disappear. Matches iOS commit 77c41d6.
  /// Per-label state for the runtime culling pass. `isPrimary` is true
  /// for the single midpoint label of each contour; "extra" labels
  /// (the ones spaced along long ridges) get hidden at mid-zoom-out so
  /// the screen-space density stays readable.
  private readonly labelEntries: {
    container: Container
    elevationFt: number
    isPrimary: boolean
  }[] = []

  constructor(features: FeatureCollection, projector: CoordinateProjector) {
    super()

    // One Graphics per (band × closed/open). Each ends up with all its
    // features' subpaths and is finalised once with a single fill+stroke /
    // stroke call — cheap on draw calls regardless of feature count. The
    // closed and open ones are kept apart so `fill()` doesn't auto-close
    // the open contour lines into chord wedges.
    const fillG = BANDS.map(() => new Graphics())
    const lineG = BANDS.map(() => new Graphics())
    // Labels live in a single container that sits on top of all the
    // contour geometry. Per-band z-ordering isn't needed for labels;
    // each label has its own background rect for legibility.
    const labelContainer = new Container()

    // Fills go down first (under), open lines on top so a small open contour
    // segment doesn't get lost beneath a filled band of the same colour.
    // Labels go last so they sit on top of every contour stroke.
    fillG.forEach((g) => this.addChild(g))
    lineG.forEach((g) => this.addChild(g))
    this.addChild(labelContainer)

    for (const feat of features.features) {
      if (!feat.geometry || feat.geometry.type !== 'LineString') continue
      const coords = (feat.geometry as LineString).coordinates
      if (coords.length < 2) continue

      const elev = elevationOf(feat)
      if (elev <= 0) continue

      const bandIdx = BANDS.findIndex((b) => elev < b.maxFt)
      if (bandIdx < 0) continue
      // bandIdx is consumed below to look up the right Graphics + the
      // stroke/fill in the finalisation passes; we don't need a local
      // `band` reference in the inner loop.

      const first = coords[0]!
      const last = coords[coords.length - 1]!
      const closed =
        coords.length >= 3 &&
        Math.abs(first[0]! - last[0]!) < CLOSED_TOLERANCE_DEG &&
        Math.abs(first[1]! - last[1]!) < CLOSED_TOLERANCE_DEG
      const g = (closed ? fillG[bandIdx] : lineG[bandIdx])!

      let started = false
      for (const [lon, lat] of coords) {
        const p = projector.project({ latitude: lat!, longitude: lon! }, true)
        if (!started) {
          g.moveTo(p.x, p.y)
          started = true
        } else {
          g.lineTo(p.x, p.y)
        }
      }
      if (closed) g.closePath()

      // Place elevation labels along the contour. Every contour gets
      // a "primary" midpoint label; long contours additionally get
      // "extra" labels spaced every LABEL_STRIDE_ALONG_CONTOUR points
      // so a ridge that spans the screen still has a tag close to
      // wherever the player is looking. The primary/extra split feeds
      // the zoom-based culling pass — at mid-zoom-out we drop the
      // extras and keep only one tag per ridge, halving the
      // screen-space label density without losing peaks entirely.
      // Skip contours below MIN_COORDS_FOR_LABEL (tiny fragments).
      if (coords.length >= MIN_COORDS_FOR_LABEL) {
        const midpointIdx = Math.floor(coords.length / 2)
        const labelPositions: { idx: number; isPrimary: boolean }[] = [
          { idx: midpointIdx, isPrimary: true },
        ]
        if (coords.length >= LABEL_STRIDE_ALONG_CONTOUR) {
          let idx = Math.floor(LABEL_STRIDE_ALONG_CONTOUR / 2)
          while (idx < coords.length) {
            // Skip the midpoint slot — it's already primary; everything
            // else along the ridge is an "extra" (cullable at low zoom).
            if (idx !== midpointIdx) {
              labelPositions.push({ idx, isPrimary: false })
            }
            idx += LABEL_STRIDE_ALONG_CONTOUR
          }
        }
        for (const { idx: labelIdx, isPrimary } of labelPositions) {
          const c = coords[labelIdx]!
          const p = projector.project(
            { latitude: c[1]!, longitude: c[0]! },
            true,
          )
          const labelNode = buildElevationLabel(elev, p)
          labelContainer.addChild(labelNode)
          this.labelEntries.push({
            container: labelNode,
            elevationFt: elev,
            isPrimary,
          })
        }
      }
    }

    // Per-band fill+stroke for closed rings, stroke-only for open lines.
    // Per-band stroke widths come from `band.lineWidth` (closed) and
    // `max(0.75, lineWidth - 0.5)` (open) — same proportion as the iOS
    // refresh: open contours 0.5pt thinner than closed of the same band.
    fillG.forEach((g, i) => {
      const b = BANDS[i]!
      g.fill({ color: b.color, alpha: b.fillAlpha })
      g.stroke({ width: b.lineWidth, color: b.color, alpha: b.strokeAlpha })
    })
    lineG.forEach((g, i) => {
      const b = BANDS[i]!
      const openWidth = Math.max(0.75, b.lineWidth - 0.5)
      g.stroke({ width: openWidth, color: b.color, alpha: b.strokeAlpha })
    })
  }

  /// Counter-scale every elevation label to keep its on-screen size
  /// constant across viewport zoom, and hide low-altitude labels at
  /// far zoom-out so mountain-peak tags aren't crowded. Called by
  /// `RadarScene` from the viewport's `zoomed` event (and once on
  /// initial layer attach). Pixi-viewport scale ∝ render size, so
  /// the inverse is the right counter-scale factor.
  ///
  /// Performance note: this iterates `labelEntries` linearly, but
  /// it only fires when the viewport scale actually changes (pinch
  /// / wheel / programmatic zoom). Steady-state baseline cost is
  /// zero. During a pinch gesture (~60 events/sec) worst-case ~500
  /// containers × Pixi transform-dirty = well within the frame
  /// budget on any modern device. iOS equivalent is
  /// `updateContourLabelStyles` (commit 77c41d6).
  updateLabelStyles(viewportScale: number): void {
    if (viewportScale <= 0) return
    const inverseScale = 1 / viewportScale
    // Stage 1 — zoom-based cull. Drops below-cutoff elevations and the
    // "extra" along-ridge labels when zoomed out. This alone wasn't
    // enough for Lukla / Himalayan terrain (hundreds of distinct
    // contour features in the 15k+ band → ~80 surviving labels at
    // fit-world even after extras and 10k cutoff).
    const far = viewportScale < FAR_ZOOM_CULL_VIEWPORT_SCALE
    const mid = viewportScale < MID_ZOOM_CULL_VIEWPORT_SCALE
    const cutoffFt = far ? FAR_CUTOFF_FT : mid ? MID_CUTOFF_FT : 0
    const decimateExtras = mid

    // Stage 2 — screen-space distance deduplication. We previously
    // used a fixed grid (Math.floor world-pos / cell-size), but cell
    // boundaries shifted continuously with viewport scale, so two
    // labels could swap "winner" status mid-zoom and the visible set
    // flickered. Distance-based dedup is stable: the criterion is
    // "no two visible labels within N screen-px of each other,
    // highest elevation wins." As zoom changes the threshold scales
    // smoothly, so labels fade in/out in order rather than jumping.
    //
    // Algorithm:
    //   1. Counter-scale every entry (cheap; sets its on-screen size).
    //   2. Apply the stage-1 cull (cutoff + extras) to filter to
    //      "candidate" entries.
    //   3. Sort candidates by elevation desc — highest peaks place
    //      first and "claim" their screen real estate.
    //   4. For each candidate in order: hide if any already-accepted
    //      label is within MIN_LABEL_SCREEN_PX (converted to world
    //      units via /viewportScale). Otherwise accept + show.
    //
    // O(N × M) where M is the number of accepted labels (~30 at
    // fit-world). Lukla has N ≈ 2k contour labels; that's ~60k
    // distance checks per zoom event — sub-millisecond on any
    // device. Same MIN_LABEL_SCREEN_PX applies at every zoom level
    // because labels are counter-scaled to a constant on-screen
    // size; that's what makes the result frame-rate stable.
    const MIN_LABEL_SCREEN_PX = 60
    const minWorldDist = MIN_LABEL_SCREEN_PX / viewportScale
    const minWorldDistSq = minWorldDist * minWorldDist

    const candidates: number[] = []
    for (let i = 0; i < this.labelEntries.length; i++) {
      const e = this.labelEntries[i]!
      e.container.scale.set(inverseScale)
      if (e.elevationFt < cutoffFt) {
        e.container.visible = false
        continue
      }
      if (decimateExtras && !e.isPrimary) {
        e.container.visible = false
        continue
      }
      candidates.push(i)
    }
    candidates.sort(
      (a, b) =>
        this.labelEntries[b]!.elevationFt - this.labelEntries[a]!.elevationFt,
    )

    const accepted: { x: number; y: number }[] = []
    for (const idx of candidates) {
      const e = this.labelEntries[idx]!
      const x = e.container.position.x
      const y = e.container.position.y
      let blocked = false
      for (const a of accepted) {
        const dx = x - a.x
        const dy = y - a.y
        if (dx * dx + dy * dy < minWorldDistSq) {
          blocked = true
          break
        }
      }
      if (blocked) {
        e.container.visible = false
      } else {
        e.container.visible = true
        accepted.push({ x, y })
      }
    }
  }
}

/// One elevation tag: cyan "<N>ft" text with a black 60%-alpha rounded
/// background rect for legibility against terrain colour bands. Positioned
/// at the projected pixel point.
function buildElevationLabel(
  elevationFt: number,
  pos: { x: number; y: number },
): Container {
  const wrapper = new Container()
  wrapper.position.set(pos.x, pos.y)

  const text = new Text({
    text: `${Math.round(elevationFt)}ft`,
    style: {
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontWeight: 'bold',
      fontSize: LABEL_FONT_SIZE,
      fill: LABEL_COLOR,
    },
  })
  // Anchor at centre so the label sits over the contour point rather
  // than to its lower-right (Pixi default is top-left).
  text.anchor.set(0.5, 0.5)

  // Background rectangle, sized from the rendered text bounds + padding.
  // Built *after* the Text so its bounds are available.
  const padding = 6
  const bg = new Graphics()
  bg.roundRect(
    -text.width / 2 - padding / 2,
    -text.height / 2 - padding / 4,
    text.width + padding,
    text.height + padding / 2,
    3,
  )
  bg.fill({ color: 0x000000, alpha: 0.6 })

  // Background under the text.
  wrapper.addChild(bg)
  wrapper.addChild(text)
  return wrapper
}

/// Pulls elevation out of a contour feature. Prefer the numeric `ELEV`
/// property (Scottsdale / Narita / SoCal contour files have this); fall back
/// to parsing the human-readable `description` ("6562 ft" style) which is
/// what the iOS reader uses.
function elevationOf(feat: Feature): number {
  const props = feat.properties as Record<string, unknown> | undefined
  if (!props) return 0
  const elev = props.ELEV
  if (typeof elev === 'number') return elev
  if (typeof elev === 'string') {
    const n = parseFloat(elev)
    if (!Number.isNaN(n)) return n
  }
  const desc = props.description
  if (typeof desc === 'string') {
    const n = parseFloat(desc.replace(/ft/i, '').trim())
    if (!Number.isNaN(n)) return n
  }
  return 0
}
